/**
 *
 * Calendar
 *
 */

import React, {
  useState,
  useRef,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import leftArrow from 'assets/icons/left-arrow.svg';
import rightArrow from 'assets/icons/right-arrow.svg';
import {
  convertToTime,
  twoWeekMilliSeconds,
  convertToDates,
  getReadableBookedTime,
  sideScroll,
  convertSecondsToMin,
} from './utils';
import LinkMessage from 'ui/components/LinkMessage';
import EventMessage from 'ui/components/EventMessage';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import calendarSmall from 'assets/icons/calendarSmall.svg';
import noCalendarSlots from 'assets/icons/no-calendar-slots.svg';
import { checkFieldValue, isEmpty } from 'utils/validation';
import closeBlackButton from 'assets/icons/closeBlackButton.svg';
import Avatar from 'react-avatar';
import useVisibility from 'utils/hooks/useVisibility';
import {
  CalendarTitle,
  SecondaryText,
  CalendarContainer,
  CalendarCloseIcon,
  LoadingOverlay,
  CalendarFieldHeader,
  FieldContainer,
  CalendarListContainer,
  ArrowContainer,
  CalendarScrollContainer,
  CalendarDate,
  CalendarDateItemWrapper,
  FadeToRight,
  FadeToLeft,
  CalendarTimeItemWrapper,
  Time,
  DashedLine,
  CalendarFieldInput,
  BookButton,
  NoSlotsWindow,
  CalendarSuccess,
  SkeletonDateContainer,
  SkeletonTimeContainer,
  ErrorMessage,
  ErrorMessageWrapper,
  FieldContainerHeader,
  CalendarForm,
  CalendarClosed,
  CalendarIconContainer,
  CalendarCloseText,
  CalendarMessageContainer,
  CalendarInfoContainer,
  CalendarActionContainer,
  SenderImageWrapper,
  MessageElementWrapper,
} from './styled';
import VisibilitySensor  from 'react-visibility-sensor';
import uuid from 'utils/uuid';
import { eventTypes } from 'utils/eventTypes';
import { saveEventToLocalStorage } from 'utils/saveToLocalStorage';
import {WINDOW_POSTMESSAGE_TYPES, TRIGGER_EVENT_TYPES} from "../../containers/App/constants";
import Tooltip from "../Tooltip";
import ReactHtmlParser from "react-html-parser";
import {postMessageToWindow} from 'utils/index';

let calendarId = uuid();
const CalendarDateItem = ({ active, disabled, isFirst, date, day, userSettingColor, onSelectSlot }) => {

  return (
    <CalendarDateItemWrapper
      disabled={disabled}
      onClick={!disabled ? onSelectSlot : undefined}
      isFirst={isFirst}
    >
      <SecondaryText margin="0" width="40px" disabled={disabled}>{day}</SecondaryText>
      <CalendarDate
        active={active}
        disabled={disabled}
        headerBackgroundColor={userSettingColor ? userSettingColor.headerBackgroundColor : null}
      >
        {date}
      </CalendarDate>
    </CalendarDateItemWrapper>
  );
};


const CalendarTimeItem = ({ time, active = true, disabled, isLast, isFirst, userSettingColor, onSlotTimeSelect }) => {
  return (
    <CalendarTimeItemWrapper isFirst={isFirst} isLast={isLast} onClick={onSlotTimeSelect}>
      <Time
        active={active}
        disabled={disabled}
        headerBackgroundColor={userSettingColor ? userSettingColor.headerBackgroundColor : null}
      >
        {time}
      </Time>
      {!isLast && <DashedLine />}
    </CalendarTimeItemWrapper>
  );
};




export const Calendar = ({
  userSettingColor,
  userSettings,
  message,
  Language,
  fetchMeetingSlots,
  bookMeetingSlot,
  processUserResponse,
  isCalendarLeftSpace,
  conversationListRef,
  userConfig,
  currentChannel,
  ...props
}) => {

  const calendarDateRef = useRef();
  const calendarTimeRef = useRef();
  const calendarRef = useRef();
  const calendarActionScrollToRef = useRef();
  const calendarTopScrollToRef = useRef();

  let isCalendarVisible = useVisibility(calendarRef.current, { scrollableEl:  conversationListRef ? conversationListRef.current : null});

  let calendar = message && message.calendar;
  if(message) message.isNewCalendar = calendar && !calendar.hasOwnProperty('booked');
  let hasBooked = calendar && calendar.hasOwnProperty('bookingId');
  let companyName = userSettings && userSettings.bot && userSettings.bot.company;
  let currentEndTime = new window.Date();
  currentEndTime.setHours(0, 0, 0, 0);

  function findPos(obj) {
    var currentTop = 0;
    if (obj.offsetParent) {
        do {
            currentTop += obj.offsetTop;
        } while (obj = obj.offsetParent);
    return [currentTop - 100];
    }
}
  let [shouldDateLeftFadeVisible, setShouldDateLeftFadeVisible] = useState(false);
  let [shouldTimeLeftFadeVisible, setShouldTimeLeftFadeVisible] = useState(false);
  let [calendarSlots, setCalendarSlots] = useState([]);
  let [activeSlot, setActiveSlot] = useState(null);
  let [activeSlotTime, setActiveSlotTime] = useState(null);
  let [additionalFields, setAdditionalFields] = useState([]);
  let [slotError, setSlotError] = useState({
    slotDateError: null,
    slotTimeError: null
  });
  let [bookingSlotTimeSpan, setBookingSlotTimeSpan] = useState({
    startTime: parseInt(window.Date.now() / 1000),
    endTime: parseInt((currentEndTime.getTime() + twoWeekMilliSeconds) / 1000),
    offset: -(currentEndTime.getTimezoneOffset() * 60),
  });

  let [isCalendarWindowClosed, setIsCalendarWindowClosed] = useState(true);

  let [showCalendarComponent, setShowCalendarComponent] = useState(!isCalendarWindowClosed);

  let [isBooking, setIsBooking] = useState(false);
  let [isSlotsLoading, setIsSlotsLoading] = useState(true);

  const [calendarErrorMessage, setCalendarErrorMessage] = useState(null)
  const [slotsApiError, setSlotsApiError] = useState(false);

  useEffect(() => {
    let isCalendarClosed = calendar && calendar.hasOwnProperty('booked') && !calendar.hasOwnProperty('bookingId')
    setIsCalendarWindowClosed(isCalendarClosed);
    setShowCalendarComponent(!isCalendarClosed)
    if (calendar && calendar.additionalFields && calendar.additionalFields.length > 0) {
      setAdditionalFields(calendar.additionalFields)
    }
  }, []);


  useEffect(() => {
    if (isCalendarVisible && showCalendarComponent && !hasBooked && calendarSlots && calendarSlots.length === 0) {
      setCalendarErrorMessage(null);
      saveCalendarEvents(eventTypes.NATIVE_CALENDAR_SHOWN);
      fetchMeetingSlots({
        meta: {
          ...bookingSlotTimeSpan,
          duration: calendar && calendar.duration || 1800,
          participants: {
            members: calendar && calendar.participants && calendar.participants.members || [],
            teams: calendar && calendar.participants && calendar.participants.teams || [],
          },
          handleNewSlotResponse,
        }
      })
      setIsSlotsLoading(true)
    }
  }, [showCalendarComponent, isCalendarVisible]);



  const saveCalendarEvents = (eventName) => {
    const event = {
      eventType: eventName,
      timestamp: window.Date.now(),
      url: userConfig.blogUrl,
      props: {
        channelId: currentChannel,
        duration: calendar && calendar.duration || 1800,
        stepUid: message.stepUid,
      },
    };
    saveEventToLocalStorage(event);
  }

  function fetchMoreSlots (isVisible) {
    if (isVisible) {
      let startTime = bookingSlotTimeSpan.endTime + 3600 * 24;
      let endTime = startTime + (twoWeekMilliSeconds / 1000);
      setBookingSlotTimeSpan({ ...bookingSlotTimeSpan, startTime, endTime });
      fetchMeetingSlots({
        meta: {
          startTime,
          endTime,
          offset: bookingSlotTimeSpan.offset,
          duration: calendar && calendar.duration || 1800,
          participants: {
            members: calendar.participants && calendar.participants.members || [],
            teams: calendar.participants && calendar.participants.teams || [],
          },
          handleNewSlotResponse,
        }
      })
    }
  }

  const handleCalendarDateScroll = (e) => {
    setShouldDateLeftFadeVisible(e.target.scrollLeft !== 0);
    if (e.target.scrollLeft) {
      window[calendarId] = e.target.scrollLeft;
    }
  }

  const handleCalendarTimeScroll = (e) => {
    setShouldTimeLeftFadeVisible(e.target.scrollLeft !== 0)
  }


  function scrollDateRight() {
    if (calendarDateRef && calendarDateRef.current) {
      saveCalendarEvents(eventTypes.NATIVE_CALENDAR_NEXT_DATES_CLICKED);
      sideScroll(calendarDateRef.current, 'right', 25, 100, 10);
    }
  };

  function scrollDateLeft() {
    if (calendarDateRef && calendarDateRef.current) {
      sideScroll(calendarDateRef.current, 'left', 25, 100, 10);
    }
  };

  function scrollTimeRight() {
    if (calendarTimeRef.current) {
      saveCalendarEvents(eventTypes.NATIVE_CALENDAR_NEXT_TIMES_CLICKED);
      sideScroll(calendarTimeRef.current, 'right', 25, 100, 10);
    }
  };

  function scrollTimeLeft() {
    if (calendarTimeRef.current) {
      sideScroll(calendarTimeRef.current, 'left', 25, 100, 10);
    }
  };



  const handleBookMeeting = (data) => {
    let bookedTime = parseInt(activeSlot.timestamp) + parseInt(activeSlotTime) - bookingSlotTimeSpan.offset;
    setIsBooking(false);
    setIsSlotsLoading(false);
    if (data.err) {
      setCalendarErrorMessage(data.errorMessage);
      return;
    };
    setCalendarErrorMessage(null);
    processUserResponse({
      bookingId: data.bookingId,
      booked: true,
      bookedTime,
      messageId: message._id || calendar.messageId,
      offset: bookingSlotTimeSpan.offset,
      additionalFields,
    }, message.type, { ...message, calendar })
  };

  const handleNewSlotResponse = (data) => {
    setIsBooking(false);
    setIsSlotsLoading(false);

    if (data.err) {
      setSlotsApiError(true);
      return
    };
    setSlotsApiError(false);
    let processedData = convertToDates(data, bookingSlotTimeSpan.offset);
    setCalendarSlots([...calendarSlots, ...processedData]);
    if (!activeSlot) {
      let newActiveSlot = processedData.find(slotData => slotData.slots && slotData.slots.length > 0);

      setActiveSlot(newActiveSlot)
      setActiveSlotTime(newActiveSlot.slots[0]);
      setSlotError({
        slotDateError: null,
        slotTimeError: null,
      });
    }
    if (calendarDateRef && calendarDateRef.current) {
      calendarDateRef.current.scrollLeft = window[calendarId];
    }
  }

  const closeCalendarWindow = () => {
    setCalendarSlots([])
    setActiveSlot(null)
    setActiveSlotTime(null);
    currentEndTime = new window.Date();
    setBookingSlotTimeSpan({
      startTime: parseInt(window.Date.now() / 1000),
      endTime: parseInt((currentEndTime.getTime() + twoWeekMilliSeconds) / 1000),
      offset: -(currentEndTime.getTimezoneOffset() * 60),
    })
    if (isCalendarWindowClosed) {
      setShowCalendarComponent(!showCalendarComponent)
      return
    };
    setIsCalendarWindowClosed(true);
    setShowCalendarComponent(false);
    processUserResponse({
      booked: false,
      messageId: message._id || calendar.messageId,
      additionalFields,
    }, message.type, { ...message, calendar });
    saveCalendarEvents(eventTypes.NATIVE_CALENDAR_CLOSED);
  }

  const isFieldValid = () => {

    let isValid = true;

    let processedField = []
    for (let i = 0; i < additionalFields.length; i++) {
      if (!additionalFields[i].hasOwnProperty('value')) additionalFields[i].value = '';
      let hasError = checkFieldValue(additionalFields[i], Language, calendar.validateDomains);
      if (hasError) {
        if (isValid) isValid = false;
        additionalFields[i].error = hasError;
      } else {
        delete additionalFields[i].error
      }
      processedField.push(additionalFields[i])
    }

    setAdditionalFields(processedField);

    let slotDateError = null, slotTimeError = null;
    if (!activeSlot) {
      isValid = false;
      slotDateError = Language && Language.CalendarSlotDateError;
    }
    if (isEmpty(activeSlotTime)) {
      isValid = false;
      slotTimeError = Language && Language.CalendarSlotTimeError;
    }
    setSlotError({
      slotDateError,
      slotTimeError,
    })
    return isValid;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFieldValid()) return;

    setCalendarErrorMessage(null)
    let meetingTime = (parseInt(activeSlot.timestamp) + parseInt(activeSlotTime)) - bookingSlotTimeSpan.offset;
    let payload = {
      meetingTime,
      duration: calendar && calendar.duration,
      participants: {
        members: calendar && calendar.participants && calendar.participants.members || [],
        teams: calendar && calendar.participants && calendar.participants.teams || [],
      },
      title: calendar && calendar.title,
      channelId: message && message.channelId,
      additionalFields: additionalFields || [],
    };
    setIsBooking(true);
    postMessageToWindow(
        {
          type: userConfig.eventListenerName,
          event: WINDOW_POSTMESSAGE_TYPES.triggerEvent,
          eventType: TRIGGER_EVENT_TYPES.calendarBooked,
          eventData: payload
        },
        '*'
    );
    bookMeetingSlot({ payload, meta: { handleBookMeeting } })
  };

  let onBookingCancel = () => {
    message.isNewCalendar = false;
    processUserResponse({
      bookingId: calendar.bookingId,
      booked: false,
      bookedTime: calendar.bookedTime,
      messageId: message._id || calendar.messageId
    }, message.type, { ...message, calendar })
    if (calendarRef.current) {
      conversationListRef.current.scroll(0,findPos(calendarRef.current));
    }
  };

  const toggleCalendarWindow = () => {
    currentEndTime = new window.Date();
      setBookingSlotTimeSpan({
        startTime: parseInt(window.Date.now() / 1000),
        endTime: parseInt((currentEndTime.getTime() + twoWeekMilliSeconds) / 1000),
        offset: -(currentEndTime.getTimezoneOffset() * 60),
      })
    if (showCalendarComponent) {
      setCalendarSlots([]);
      setActiveSlot(null);
      setActiveSlotTime(null);
    }
    if (!showCalendarComponent && calendarRef.current) {
      conversationListRef.current.scroll(0,findPos(calendarRef.current));
    }

    setShowCalendarComponent(!showCalendarComponent)
  };

  const renderDateSlots = (
    <FieldContainer margin="0 0 20px">
      <FieldContainerHeader>
        <CalendarFieldHeader>{Language && Language.CalendarPickADate && Language.CalendarPickADate.toString()}</CalendarFieldHeader>
        {slotError.slotDateError && (<ErrorMessageWrapper position="relative" margin="0 6px 10px">
          <ErrorMessage>{slotError.slotDateError}</ErrorMessage>
        </ErrorMessageWrapper>)}
      </FieldContainerHeader>

      <CalendarListContainer>
        <ArrowContainer onClick={scrollDateLeft}>
          {shouldDateLeftFadeVisible && <FadeToRight />}
          <img src={leftArrow} />
        </ArrowContainer>
        <CalendarScrollContainer onScroll={handleCalendarDateScroll} ref={calendarDateRef} id="scroll">
          {isEmpty(calendarSlots) ? new Array(14).fill(14).map((val, valId) => {
            return (
              <SkeletonDateContainer key={valId}>
                <SkeletonTheme color="#2F3C4E33" highlightColor="#2F3C4E08">
                  <Skeleton height="10px" width="30px" duration={3} />
                  <div></div>
                  <Skeleton height="20px" width="30px" duration={3} />
                </SkeletonTheme>
              </SkeletonDateContainer>
            );
          }) : calendarSlots.map((slot, slotsId) => {
            let onSelectSlot = () => {
              setActiveSlot(slot);
              setActiveSlotTime(slot.slots[0]);
              if (calendarTimeRef.current) calendarTimeRef.current.scrollLeft = 0;
              setSlotError({
                ...slotError,
                slotDateError: null
              });
            }
            return (
              <CalendarDateItem
                userSettingColor={userSettingColor}
                date={slot.date}
                isFirst={slotsId === 0}
                active={parseInt(slot.timestamp) === (activeSlot && parseInt(activeSlot.timestamp))}
                disabled={slot.disabled}
                day={slot.day}
                onSelectSlot={onSelectSlot}
                key={slotsId}
              />
            );
          })}
          <VisibilitySensor onChange={fetchMoreSlots}>
            <SkeletonDateContainer>
              <SkeletonTheme color="#2F3C4E33" highlightColor="#2F3C4E08">
                <Skeleton height="10px" width="30px" duration={3} />
                <div></div>
                <Skeleton height="20px" width="30px" duration={3} />
              </SkeletonTheme>
            </SkeletonDateContainer>
          </VisibilitySensor>
        </CalendarScrollContainer>
        <ArrowContainer onClick={scrollDateRight}>
          <FadeToLeft />
          <img src={rightArrow} />
        </ArrowContainer>
      </CalendarListContainer>
    </FieldContainer>
  );

  const renderTimeSlots = (
    <FieldContainer margin="0 0 20px">
      <FieldContainerHeader>
        <CalendarFieldHeader>{Language && Language.CalendarSelectTime && Language.CalendarSelectTime.toString()}</CalendarFieldHeader>
        {slotError.slotTimeError && (<ErrorMessageWrapper position="relative" margin="0 6px 10px">
          <ErrorMessage>{slotError.slotTimeError}</ErrorMessage>
        </ErrorMessageWrapper>)}
      </FieldContainerHeader>
      <CalendarListContainer>
        <ArrowContainer height="22px" onClick={scrollTimeLeft}>
          {shouldTimeLeftFadeVisible && <FadeToRight height="22px" />}
          <img src={leftArrow} />
        </ArrowContainer>
        <CalendarScrollContainer onScroll={handleCalendarTimeScroll} ref={calendarTimeRef}>
          {isEmpty(activeSlotTime) ? new Array(14).fill(14).map((val, valId) => {
            return (
              <SkeletonTheme color="#2F3C4E33" highlightColor="#2F3C4E08" key={valId}>
                <SkeletonTimeContainer isFirst={valId === 0}>
                  <Skeleton height="20px" width="60px" duration={3} />
                  <DashedLine />
                </SkeletonTimeContainer>
              </SkeletonTheme>
            );
          }) : activeSlot && activeSlot.slots && activeSlot.slots.map((slotTime, slotTimeId) => {

            const selectSlotTime = () => {
              setActiveSlotTime(slotTime)
              setSlotError({
                ...slotError,
                slotTimeError: null
              });
            }
            return (
              <CalendarTimeItem
                userSettingColor={userSettingColor}
                active={slotTime === activeSlotTime}
                time={convertToTime(slotTime)}
                isFirst={slotTimeId === 0}
                isLast={slotTimeId === activeSlot.slots.length - 1}
                onSlotTimeSelect={selectSlotTime}
                key={slotTimeId}
              />
            );
          })}
        </CalendarScrollContainer>
        <ArrowContainer height="22px" onClick={scrollTimeRight}>
          <FadeToLeft height="22px" />
          <img src={rightArrow} />
        </ArrowContainer>
      </CalendarListContainer>
    </FieldContainer>
  );

  const renderAdditionalField = (
    <CalendarForm
      onSubmit={handleSubmit}>
      {additionalFields && additionalFields.length > 0 && additionalFields.map((field, fieldId) => {
        const fieldIndex = additionalFields.findIndex(additionalField => additionalField.key === field.key);

        const updateFields = (e) => {
          const updatedFields = [...additionalFields];
          if (fieldIndex === -1) {
            updatedFields.push({
              key: field.key,
              name: field.name,
              type: field.type,
              value: e.target.value,
              fieldType: field.fieldType,
              error: null,
            });
          } else {
            updatedFields[fieldIndex] = {
              ...updatedFields[fieldIndex],
              value: e.target.value,
              fieldType: field.fieldType,
              error: null,
            };
            let hasError = checkFieldValue(updatedFields[fieldIndex], Language, calendar.validateDomains);
            if (hasError) {
              updatedFields[fieldIndex].error = hasError;
            } else {
              delete updatedFields[fieldIndex].error;
            }
          }
          setAdditionalFields(updatedFields);
        }

        const handleChange = (e, fieldType) => {
          if (fieldType === "number") {
            const regex = /^[0-9\b]+$/;
            if (e.target.value === '' || regex.test(e.target.value)) {
              updateFields(e);
            }
          } else {
            updateFields(e);
          }
        }
        return (
          <FieldContainer margin="0 0 20px" key={fieldId}>
            <CalendarFieldHeader>{field.name}</CalendarFieldHeader>
            <CalendarFieldInput
              type={field.type}
              placeholder={Language && Language.CalendarFieldInputPlaceHolder && Language.CalendarFieldInputPlaceHolder.toString().replace("{field.name}", field.name)}
              value={field.value}
              onChange={(e) => handleChange(e, field.fieldType)}
            />
            {additionalFields[fieldIndex] && additionalFields[fieldIndex].error && (
              <ErrorMessageWrapper>
                <ErrorMessage>{additionalFields[fieldIndex].error}</ErrorMessage>
              </ErrorMessageWrapper>
            )}
          </FieldContainer>
        );
      })}
      {calendarErrorMessage && (
        <FieldContainer margin="0 0 8px">
          <ErrorMessageWrapper position="relative">
            <ErrorMessage>{calendarErrorMessage}</ErrorMessage>
          </ErrorMessageWrapper>
        </FieldContainer>

      )}
      <BookButton
        backgroundColor={userSettingColor ? userSettingColor.headerBackgroundColor : null}
        type="submit"
      >
        {isBooking ? Language && Language.CalendarBooking : Language && Language.CalendarBook}
      </BookButton>
    </CalendarForm>
  );

  const calendarWindowHeight = slotsApiError && '420px';
  const renderNoSlotsWindow = (
      <NoSlotsWindow>
        <img src={noCalendarSlots} />
        <CalendarTitle>
          {Language && Language.CalendarEmpty}
        </CalendarTitle>
        <SecondaryText
          margin="0 0 76px"
        >
          {Language && Language.CalendarNoSlotsAvailable}
        </SecondaryText>
        <BookButton
          textColor={userSettingColor ? userSettingColor.headerBackgroundColor : null}
          backgroundColor='#ffffff'
          boxShadowColor="#353b471a"
          onClick={closeCalendarWindow}
        >
          {Language && Language.Cancel}
        </BookButton>
      </NoSlotsWindow>
  );

  const defaultCalendarWindowHeight = '500px'; // Default height for the calendar window

  return (
    <CalendarMessageContainer id="calendar" ref={calendarRef}>
      <CalendarInfoContainer>
        <div ref={calendarTopScrollToRef}/>
        {calendar && calendar.hasOwnProperty('booked') && !isCalendarLeftSpace && (
          <SenderImageWrapper
            margin={showCalendarComponent ? "0 0 10px 0" : "0"}
          >
            <Avatar src={message.img} name={message.name || 'Agent'} size="30" round={true} />
          </SenderImageWrapper>
        )}
        <MessageElementWrapper isCalendarLeftSpace={isCalendarLeftSpace}>
          {calendar && calendar.hasOwnProperty('booked') && calendar.bookingId ? (
            <CalendarSuccess >
              <LinkMessage
                message={{
                  link: {
                    title: calendar && calendar.title,
                    subtitle: `${getReadableBookedTime(calendar.bookedTime, calendar.bookedTime + calendar.duration, false)}`
                  }
                }}
                userSettingColor={userSettingColor}
                isDisabled={!calendar.booked}
              />
            </CalendarSuccess>
          ) : isCalendarWindowClosed && (
            <CalendarClosed
              margin={showCalendarComponent ? "0 0 10px 0" : "0"}
              onClick={toggleCalendarWindow}
              headerBackgroundColor={userSettingColor ? userSettingColor.headerBackgroundColor : null}
            >
              <CalendarIconContainer
                headerBackgroundColor={userSettingColor ? userSettingColor.headerBackgroundColor : null}
              >
                <img src={calendarSmall} />
              </CalendarIconContainer>
              <CalendarCloseText>{ReactHtmlParser(calendar && calendar.title)}</CalendarCloseText>
            </CalendarClosed>
          )}

        </MessageElementWrapper>

      </CalendarInfoContainer>
      <CalendarActionContainer>
        {calendar && calendar.hasOwnProperty('booked') && calendar.bookingId && (
          <CalendarSuccess>

            {calendar.booked ? (
              <EventMessage
                message={{ event: Language && Language.MeetingScheduledWith.toString().replace("{companyName}", companyName)}}
                Language={Language}
                userSettingColor={userSettingColor}
                isCancelable={true}
                onCancelEvent={onBookingCancel}
              />
            ) : (
                <EventMessage
                  message={{ event: calendar.title + Language ? Language.MeetingCancelled : '' }}
                  Language={Language}
                  userSettingColor={userSettingColor}
                />
              )}
          </CalendarSuccess>
        )}
        {calendar && !calendar.bookingId && showCalendarComponent &&
          <CalendarContainer
            height={calendarWindowHeight || defaultCalendarWindowHeight}
          >
            {(isSlotsLoading || isBooking) && <LoadingOverlay />}
            <CalendarCloseIcon
              src={closeBlackButton}
              onClick={closeCalendarWindow}
            />


            {slotsApiError ? renderNoSlotsWindow : (
              <>
                <Tooltip title={ReactHtmlParser(calendar.title)} placement="top">
                  <CalendarTitle>
                    {ReactHtmlParser(calendar.title)}
                  </CalendarTitle>
                </Tooltip>
                <SecondaryText>
                  {Language
                  && Language.ScheduleMeeting
                  && Language.ScheduleMeeting.toString()
                      .replace("{MINUTES}",convertSecondsToMin(calendar.duration) || '')
                      .replace("{TIME}",`${(activeSlot && getReadableBookedTime((activeSlot.timestamp - bookingSlotTimeSpan.offset) + activeSlotTime, calendar.bookedTime + calendar.duration, true, true)) || ''} ${(!isEmpty(activeSlotTime)?convertToTime(activeSlotTime):'')}`)}
                </SecondaryText>
                {renderDateSlots}
                {renderTimeSlots}
                {renderAdditionalField}
              </>
            )}
            <div ref={calendarActionScrollToRef}></div>
          </CalendarContainer>}
      </CalendarActionContainer>
    </CalendarMessageContainer>
  );
}

Calendar.propTypes = {
  userSettingColor: PropTypes.object,
  userSettings: PropTypes.object,
  message: PropTypes.object,
  fetchMeetingSlots: PropTypes.func,
  bookMeetingSlot: PropTypes.func,
  processUserResponse: PropTypes.func,
  isCalendarLeftSpace: PropTypes.bool,
};

export default Calendar;



